/**
 * The palette laid out the way the marked palette plate is: one square
 * swatch per entry at its plate row / column, numbered by its palette
 * index, with the sample tooltip (design-space parameters, marking time).
 *
 * Edit colors assigns with it (one swatch highlighted: the assigned one),
 * the random assignment dialog picks with it (every chosen swatch
 * highlighted). ``label`` names a swatch for assistive technology,
 * ``onPick`` receives the clicked entry, ``busy`` blocks clicks while a
 * request runs.
 */
import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import PaletteSampleTooltip from "./PaletteSampleTooltip";

const present = (value) => value !== null && value !== undefined;

export default function PaletteSwatchGrid({
  palette, columns, highlighted, onPick, label, busy = false, testId = "palette-grid",
}) {
  const gridColumns = Math.max(1, columns || 1);
  const pick = (entry) => {
    if (!busy) onPick(entry);
  };
  return (
    <Box
      data-testid={testId}
      data-columns={gridColumns}
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
        gap: 0.5,
        width: "100%",
        maxWidth: gridColumns * 44,
      }}
    >
      {palette.map((entry, position) => {
        const active = Boolean(highlighted && highlighted(entry));
        const row = present(entry.row) ? entry.row : Math.floor(position / gridColumns);
        const column = present(entry.column) ? entry.column : position % gridColumns;
        return (
          <Tooltip
            key={entry.sample_id}
            placement="top"
            title={<PaletteSampleTooltip entry={entry} />}
          >
            <Box
              role="button"
              tabIndex={0}
              aria-label={label(entry)}
              aria-pressed={active}
              data-row={row}
              data-column={column}
              // Keep the focus where it is (the clusters input on Edit
              // colors): its blur save would otherwise race the click.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(entry)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  pick(entry);
                }
              }}
              sx={{
                gridRow: row + 1, gridColumn: column + 1,
                width: "100%", aspectRatio: "1 / 1", minWidth: 0, borderRadius: 1,
                bgcolor: entry.hex,
                border: active ? "2px solid" : "1px solid rgba(15, 23, 42, 0.2)",
                borderColor: active ? "primary.main" : "rgba(15, 23, 42, 0.2)",
                boxShadow: active ? "inset 0 0 0 2px rgba(255, 255, 255, 0.9)" : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.6 : 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: gridColumns > 10 ? "0.625rem" : "0.75rem",
                  lineHeight: 1,
                  color: "#ffffff",
                  textShadow: "0 0 3px rgba(0,0,0,0.9)",
                }}
              >
                {entry.index}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
